import React from 'react';
import { RouteComponentProps, Switch, Route } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import {
  createStyles, WithStyles, withStyles, Theme,
} from '@material-ui/core/styles';

import LimberApi from '../api/LimberApi';
import { OrganizationDetails } from '../generated-api';
import withElements, { ElementsProps } from '../utils/withElements';
import PurchaseButton from '../components/PurchaseButton';

interface RouterParams {
  organizationSlug: string;
}

interface Props extends RouteComponentProps<RouterParams>,
  WithStyles<typeof styles>, ElementsProps {
}

interface State {
  organization?: OrganizationDetails;
}

export class OrganizationPaymentView extends
  React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
    };
  }

  componentDidMount() {
    this.loadOrganization();
  }

  getOrganizationSlug(): string {
    const { organizationSlug } = this.props.match.params;
    return organizationSlug;
  }

  async loadOrganization() {
    const organization = await LimberApi.organizationDetailsGet({
      organizationSlug: this.getOrganizationSlug(),
    });
    this.setState({ organization });
  }

  render(): React.ReactNode {
    const { classes, match: { path } } = this.props;
    const { organization } = this.state;

    if (organization == null) return null;
    const { programPriceAmount, programPriceCurrency } = organization;
    if (programPriceAmount == null || programPriceCurrency == null) return null;

    const programPriceStr = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: programPriceCurrency,
    }).format(programPriceAmount / 100);

    return (
      <Switch>
        <Route exact path={`${path}/success`}>
          <Box className={`${classes.section} ${classes.headerSection}`}>
            <Container maxWidth="md">
              <Typography variant="h1">
                Payment successful
              </Typography>
            </Container>
          </Box>
          <Box className={classes.section}>
            <Container maxWidth="md">
              <Typography variant="body1" className={classes.body}>
                Check your email for a personalized link to start using Limber.
              </Typography>
            </Container>
          </Box>
        </Route>
        <Route path={path}>
          <Box className={`${classes.section} ${classes.headerSection}`}>
            <Container maxWidth="md">
              <Typography variant="h1">
                {organization.isPublic ? (
                  'You have been invited to join Limber!'
                ) : (
                  `${organization.name} has invited you to join Limber!`
                )}
              </Typography>
            </Container>
          </Box>
          <Box className={classes.section}>
            <Container maxWidth="md">
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <img
                    src={require('../assets/images/logo.png')}
                    alt="Limber"
                    className={classes.logo}
                  />
                  <Typography variant="h2">
                    Experiencing joint or muscle pain?
                  </Typography>
                  <Typography variant="body1" className={classes.body}>
                    Complete a personalized exercise therapy program from
                    the comfort of your home.
                  </Typography>
                  <Typography variant="h3" className={classes.dash}>
                    &ndash;&ndash;
                  </Typography>
                  <Typography variant="h3">
                    Programs for low back, shoulder, hip, neck, or knee
                    pain.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={7}>
                  <img
                    /* eslint-disable-next-line max-len */
                    src={require('../assets/images/payment-page/Limber Musculoskeletal Header Photo.png')}
                    alt={
                      'Smartphone with the Limber app open; next to it a '
                      + 'man holding his arm and smiling.'
                    }
                    className={classes.fullWidthGraphic}
                  />
                </Grid>
              </Grid>
            </Container>
          </Box>
          <Box className={`${classes.section} ${classes.purchaseSection}`}>
            <Container maxWidth="md">
              <PurchaseButton organization={organization} />
            </Container>
          </Box>
          <Box className={classes.section}>
            <Container maxWidth="md">
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Typography variant="h2">
                    Exercise therapy made simple.
                  </Typography>
                  <Typography variant="body1" className={classes.body}>
                    Follow along video programs guided on-screen by an
                    expert Physical Therapist.
                  </Typography>
                  <Typography variant="body1" className={classes.body}>
                    Program details:
                    <ul>
                      <li>8 weeks</li>
                      <li>3 sessions per week</li>
                    </ul>
                  </Typography>
                </Grid>
                <Grid item xs={12} md={7}>
                  <img
                    /* eslint-disable-next-line max-len */
                    src={require('../assets/images/payment-page/Exercise Therapy Video Screenshot.png')}
                    alt={
                      'Smartphone showing a video of a woman performing '
                      + 'physical therapy exercises.'
                    }
                    className={classes.fullWidthGraphic}
                  />
                </Grid>
              </Grid>
            </Container>
          </Box>
          <Box className={`${classes.section} ${classes.purchaseSection}`}>
            <Container maxWidth="md">
              <PurchaseButton organization={organization} />
            </Container>
          </Box>
          <Box className={classes.section}>
            <Container maxWidth="md">
              <Typography
                variant="body1"
                className={`${classes.body} ${classes.stepsHeader}`}
              >
                How the Limber app works:
              </Typography>
              <Grid container spacing={2} className={classes.oppositeStep}>
                <Grid item xs={12} md={6} className={classes.step}>
                  <Typography variant="h2" className={classes.stepNumber}>
                    1
                  </Typography>
                  <Typography variant="body1" className={classes.body}>
                    <strong>First complete a brief medical assessment, </strong>
                    created by our team of doctors.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <img
                    src={require('../assets/images/payment-page/step1.png')}
                    alt={
                      'Limber app screenshot showing a medical assessment '
                      + 'questionnaire.'
                    }
                    className={classes.fullWidthGraphic}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6} className={classes.step}>
                  <Typography variant="h2" className={classes.stepNumber}>
                    2
                  </Typography>
                  <Typography variant="body1" className={classes.body}>
                    After learning more about your condition and goals, Limber
                    {' '}
                    <strong>
                      tailors an exercise therapy program for you,
                    </strong>
                    {' '}which focuses on a scientific approach of reducing
                    pain, restoring motion, and advancing strength.
                  </Typography>
                  <Typography variant="body1" className={classes.body}>
                    <em>
                      Your program includes a series of easy-to-follow videos
                      that become increasingly more challenging.
                    </em>
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <img
                    src={require('../assets/images/payment-page/step2.png')}
                    alt={
                      'Limber app screenshot showing a list of videos '
                      + 'with some of them checked off.'
                    }
                    className={classes.fullWidthGraphic}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} className={classes.oppositeStep}>
                <Grid item xs={12} md={6} className={classes.step}>
                  <Typography variant="h2" className={classes.stepNumber}>
                    3
                  </Typography>
                  <Typography variant="body1" className={classes.body}>
                    <strong>
                      Begin your program from the comfort of your home.
                    </strong>
                    {' '}Every video is guided on-screen by an expert Physical
                    Therapist.
                  </Typography>
                  <Typography variant="body1" className={classes.body}>
                    <em>Sayonara to dull home therapy!</em>
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <img
                    src={require('../assets/images/payment-page/step3.png')}
                    alt={
                      'Limber screenshot showing a single exercise video '
                      + 'with duration and required equipment.'
                    }
                    className={classes.fullWidthGraphic}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6} className={classes.step}>
                  <Typography variant="h2" className={classes.stepNumber}>
                    4
                  </Typography>
                  <Typography variant="body1" className={classes.body}>
                    <strong>
                      Limber tracks your progress and adjusts your program
                    </strong>
                    {' '}based on clinically validated measures.
                  </Typography>
                  {!organization.isPublic && (
                    <Typography variant="body1" className={classes.body}>
                      <em>
                        Progress reports are also provided to your physician,
                        including number of sessions completed and your pain
                        and function measures.
                      </em>
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <img
                    src={require('../assets/images/payment-page/step4.png')}
                    alt={
                      'Limber screenshot with sessions completed, streak '
                      + 'and physical function score graph.'
                    }
                    className={classes.fullWidthGraphic}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} className={classes.oppositeStep}>
                <Grid item xs={12} md={6} className={classes.step}>
                  <Typography variant="h2" className={classes.stepNumber}>
                    5
                  </Typography>
                  <Typography variant="body1" className={classes.body}>
                    You're on the evidence-based path to{' '}
                    <strong>
                      reduce pain, resume normal activities, &amp;
                      minimize the risk of future injury.
                    </strong>
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <img
                    src={require('../assets/images/payment-page/step5.png')}
                    alt={
                      'Limber screenshot that says "Congratulations on '
                      + 'completed the program!".'
                    }
                    className={classes.fullWidthGraphic}
                  />
                </Grid>
              </Grid>
            </Container>
          </Box>
          <Box className={`${classes.section} ${classes.purchaseSection}`}>
            <Container maxWidth="md">
              <PurchaseButton organization={organization} />
            </Container>
          </Box>
          <Box className={classes.section}>
            <Container maxWidth="md">
              <Grid container spacing={2}>
                <Grid item xs={12} md={6} className={classes.step}>
                  <Typography variant="body1" className={classes.body}>
                    <strong>
                      People who take an active approach to recovery report
                      improved quality of life, less pain, and a reduced need
                      for medications, injections, and surgery.
                    </strong>
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <img
                    /* eslint-disable-next-line max-len */
                    src={require('../assets/images/payment-page/Musculoskeletal Hiking Photo.png')}
                    alt="A woman hiking."
                    className={classes.fullWidthGraphic}
                  />
                </Grid>
              </Grid>
            </Container>
          </Box>
          <Box className={`${classes.section} ${classes.footerSection}`}>
            <Container maxWidth="md">
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" className={classes.body}>
                    &copy; Limber Health, Inc.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" className={classes.contact}>
                    <a href="mailto:support@limberhealth.com">
                      <i className={`fa fa-envelope ${classes.contactIcon}`} />
                      support@limberhealth.com
                    </a>
                  </Typography>
                </Grid>
              </Grid>
            </Container>
          </Box>
        </Route>
      </Switch>
    );
  }
}

const styles = (theme: Theme) => createStyles({
  section: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    color: theme.palette.primary.main,
    '& p, & h2, & h3, & img': {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    '& h3': {
      fontWeight: 600,
      textTransform: 'uppercase',
    },
  },
  headerSection: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    [theme.breakpoints.down('sm')]: {
      '& h1': {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
      },
    },
  },
  footerSection: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  purchaseSection: {
    backgroundColor: theme.palette.grey[200],
    transition: '250ms',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      '& .MuiButton-root': {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.primary.main,
      },
      '& .Mui-disabled ': {
        color: theme.palette.action.disabled,
      },
    },
  },
  body: {
    fontSize: 20,
  },
  logo: {
    height: 76,
  },
  dash: {
    // Arial joins dashes with no space between.
    fontFamily: 'Arial',
  },
  fullWidthGraphic: {
    width: '100%',
    objectFit: 'contain',
  },
  stepsHeader: {
    fontWeight: 'bold',
    textAlign: 'center',
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.up('md')]: {
      paddingBottom: theme.spacing(4),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(-4),
    },
  },
  oppositeStep: {
    flexDirection: 'row-reverse',
  },
  stepNumber: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    lineHeight: '66px',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      alignSelf: 'center',
    },
  },
  contact: {
    fontSize: 20,
    '& a': {
      color: theme.palette.getContrastText(theme.palette.primary.main),
      textDecoration: 'none',
    },
    [theme.breakpoints.up('md')]: {
      textAlign: 'right',
    },
  },
  contactIcon: {
    fontSize: 28,
    position: 'relative',
    top: 4,
    display: 'inline-block',
    marginRight: theme.spacing(2),
  },
});

export default withStyles(styles)(withElements(OrganizationPaymentView));
